/**
 * Decides when a tab becoming visible should trigger a sync.
 *
 * - Window `focus` is deliberately NOT a trigger: it fires on every click back from DevTools or
 *   another window, which caused a full sync per click.
 * - Socket healthy: realtime keeps data fresh, so skip unless the last successful sync is older
 *   than `freshMs` (60 s).
 * - Socket down: sync right away, but at most once per `minGapMs` (5 s).
 */
export interface VisibilityThrottleOptions {
  isConnected(): boolean;
  now?(): number;
  freshMs?: number;
  minGapMs?: number;
}

export const VISIBLE_FRESH_MS = 60_000;
export const VISIBLE_MIN_GAP_MS = 5_000;

export class VisibilitySyncThrottle {
  private lastSuccess = Number.NEGATIVE_INFINITY;
  private lastTriggered = Number.NEGATIVE_INFINITY;
  private readonly now: () => number;

  constructor(private readonly o: VisibilityThrottleOptions) {
    this.now = o.now ?? Date.now;
  }

  /** Record a successful sync completion (from any trigger). */
  markSuccess() {
    this.lastSuccess = this.now();
  }

  /** Called on hidden → visible; returns true when a sync should run now. */
  shouldSync(): boolean {
    const t = this.now();
    if (this.o.isConnected()) {
      if (t - this.lastSuccess < (this.o.freshMs ?? VISIBLE_FRESH_MS)) return false;
    } else if (t - this.lastTriggered < (this.o.minGapMs ?? VISIBLE_MIN_GAP_MS)) {
      return false;
    }
    this.lastTriggered = t;
    return true;
  }
}

/** Wire `visibilitychange` (hidden → visible only) to `onVisible`; returns a detach function. */
export function onBecameVisible(doc: Document, onVisible: () => void): () => void {
  let wasHidden = doc.visibilityState === "hidden";
  const fn = () => {
    const hidden = doc.visibilityState === "hidden";
    if (wasHidden && !hidden) onVisible();
    wasHidden = hidden;
  };
  doc.addEventListener("visibilitychange", fn);
  return () => doc.removeEventListener("visibilitychange", fn);
}
